import type { AnalyticsQuery } from "schemas/generated/analyticsRead";
import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import MetricWithDelta from "@editor/components/analytics/MetricWithDelta";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { generateDeepDiveNavigationPathFromUrl } from "@editor/utils/analytics";

import { DEFAULT_QUERY } from "@/features/analytics/constants";
import useAnalyticsStatus from "@/features/analytics/useAnalyticsStatus";
import useBasicAnalyticsRead from "@/features/analytics/useBasicAnalyticsRead";
import useUrlHosts from "@/features/analytics/useUrlHosts";
import IconButton from "@replo/design-system/components/button/IconButton";
import { getRangesFromTimePeriod } from "analytics-utils";
import { LineChart, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const usePageAnalytics = ({
  url,
  path,
}: {
  url: string | null;
  path: string;
}) => {
  const { workspaceId, workspace } = useCurrentWorkspaceContext();
  const { project } = useCurrentProjectContext();
  const projectId = project?.id;

  const { isLoading: isLoadingAnalyticsStatus, shouldBlurFullScreen } =
    useAnalyticsStatus();

  const urlHostsObj = useUrlHosts();

  const { urlSyncedQuery, deepDivePathWithCompressedUrlSyncedQuery } =
    generateDeepDiveNavigationPathFromUrl({
      url,
      urlHosts: urlHostsObj.data,
      path,
      workspaceId: workspaceId ?? "",
      projectId: projectId ?? null,
      selectedTimePeriod: {
        type: "relative",
        value: "last-7-days",
      },
    });

  const { updatedRanges: ranges } = getRangesFromTimePeriod({
    selectedTimePeriod: urlSyncedQuery.selectedTimePeriod,
    timeZone: project?.timeZone ?? workspace?.timeZone ?? "UTC",
  });

  const query: AnalyticsQuery = {
    ...DEFAULT_QUERY,
    urlHosts: urlSyncedQuery.urlHosts,
    ranges,
    filters: urlSyncedQuery.filters,
    mode: "groupByUrlPath",
    isFractional: false,
  };

  const { mainRangeResults, isLoading: isLoadingAnalytics } =
    useBasicAnalyticsRead({
      type: "withoutUrlSyncQuery",
      queryConfig: query,
    });

  const metrics = mainRangeResults[0]?.metrics;
  const sessions = metrics?.unique_sessions?.[0] ?? null;
  const earnings = metrics?.revenue?.[0] ?? null;
  const conversionRate = metrics?.conversion_rates?.[0] ?? null;

  return {
    sessions,
    earnings,
    conversionRate,
    isLoading: isLoadingAnalytics || isLoadingAnalyticsStatus,
    deepDivePathWithCompressedUrlSyncedQuery,
    shouldBlurFullScreen,
  };
};

export default function PageCardAnalytics({
  file,
  publishedSiteUrl,
}: {
  file: FileWithLatestFileVersion;
  publishedSiteUrl?: string;
}) {
  const projectId = useCurrentProjectId();
  const {
    sessions,
    // earnings,
    // conversionRate,
    deepDivePathWithCompressedUrlSyncedQuery,
    shouldBlurFullScreen,
  } = usePageAnalytics({
    url: publishedSiteUrl ?? null,
    path: file.pageRoute ?? "",
  });
  const navigate = useNavigate();

  const metricsToDisplay = [
    {
      label: "Sessions",
      value: sessions,
      metricName: "unique_sessions" as const,
      fallbackValue: "0",
    },
    // NOTE (Ben, 2025-09-15): We're hiding earnings and conversion rate for now since they don't work on V2 yet
    // {
    //   label: "Earnings",
    //   value: earnings,
    //   metricName: "revenue" as const,
    //   fallbackValue: 0,
    // },
    // {
    //   label: "Conversion",
    //   value: conversionRate,
    //   metricName: "conversion_rates" as const,
    //   fallbackValue: undefined,
    // },
  ];

  return (
    <div className="relative w-full">
      <div className="grid grid-cols-3 w-full">
        {metricsToDisplay.map((metric) => (
          <div key={metric.label} className="space-y-0.5">
            <p className="text-sm text-placeholder">{metric.label}</p>
            <p className="header-h4">
              <MetricWithDelta
                name={metric.metricName}
                value={metric.value}
                doesCompareValueExist={false}
                valueClassName={shouldBlurFullScreen ? "blur-xs" : undefined}
              />
            </p>
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 right-0">
        <IconButton
          variant="tertiary"
          size="sm"
          icon={
            shouldBlurFullScreen ? <Lock size={16} /> : <LineChart size={16} />
          }
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            if (shouldBlurFullScreen) {
              if (!projectId) {
                return;
              }

              navigate(`/shops/${projectId}/analytics/insights`);
            } else {
              if (!deepDivePathWithCompressedUrlSyncedQuery) {
                return;
              }

              navigate(deepDivePathWithCompressedUrlSyncedQuery);
            }
          }}
          tooltipText={
            shouldBlurFullScreen ? "Upgrade for analytics" : "Last 7 days"
          }
        />
      </div>
    </div>
  );
}
